import { useState } from "react";
import { CaretDown, CaretRight, Warning } from "@phosphor-icons/react";

import { useLang } from "../../contexts/LanguageContext";
import { diagnostiquerEchec } from "../../lib/diagnosticEchec";
import { pipelineStageLabel } from "../../lib/videoDetailFormat";

/**
 * Tâche 1400 (Eric 2026-09-30) — La raison lisible d'un échec de pipeline,
 * avec la trace brute conservée en dessous.
 *
 * La trace n'est pas cachée par confort d'affichage : c'est la PREUVE, et
 * c'est elle qu'on transmet quand la cause n'est pas reconnue. Elle est
 * repliée par défaut parce qu'une commande ffmpeg de quarante lignes posée
 * en haut de la fiche vidéo empêche de lire quoi que ce soit d'autre.
 */
export function DiagnosticEchecPanel({ video, compact = false }) {
  const { tr, lang } = useLang();
  const [ouvert, setOuvert] = useState(false);
  const d = diagnostiquerEchec(video);

  const titre = d.reconnu
    ? (lang === "en" ? d.titre_en : d.titre)
    : tr(
        `Échec à l'étape « ${pipelineStageLabel(d.etape, "fr")} » — cause non reconnue`,
        `Failed at step "${pipelineStageLabel(d.etape, "en")}" — cause not recognised`,
      );

  return (
    <div className="min-w-0" data-testid="diagnostic-echec" data-diagnostic-id={d.id}>
      <h4 className="text-sm font-bold text-red-900 flex items-start gap-2" data-testid="diagnostic-echec-titre">
        {!compact && <Warning size={16} className="text-red-700 shrink-0 mt-0.5" weight="fill" />}
        <span className="min-w-0">{titre}</span>
      </h4>

      <p className="text-xs text-red-800 mt-1 leading-relaxed" data-testid="diagnostic-echec-cause">
        {lang === "en" ? d.cause_en : d.cause}
      </p>

      <p className="text-xs text-red-900 mt-1.5 leading-relaxed" data-testid="diagnostic-echec-action">
        <span className="font-semibold">{tr("À faire : ", "What to do: ")}</span>
        {lang === "en" ? d.action_en : d.action}
      </p>

      {d.technique && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOuvert((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 hover:text-red-900 hover:underline"
            aria-expanded={ouvert}
            data-testid="diagnostic-echec-details-toggle"
          >
            {ouvert ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
            {tr("Détails techniques", "Technical details")}
          </button>
          {ouvert && (
            <pre
              className="mt-1.5 text-[10px] text-red-800/90 bg-white/70 border border-red-200 rounded p-2 font-mono whitespace-pre-wrap break-all leading-relaxed max-h-48 overflow-y-auto"
              data-testid="diagnostic-echec-technique"
            >
              {d.technique}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

export default DiagnosticEchecPanel;
